import { Checkbox } from '@creditornot/cb-checkbox';
import { cbSemanticCssVars, colors } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React, { useContext, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Category, Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { useResizeObserver } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { hasPermissions, usePermissions } from 'common/wolt-permissions';
import LazyLoadedThumbnail from 'modules/menu/components/LazyLoadedThumbnail';
import { MENU_MOBILE_BREAKPOINT, WOLT_SPECIALS_CATEGORY_ID } from 'modules/menu/constants';
import { solidify } from 'modules/menu/solidify';
import useMenuData from 'modules/menu/useMenuData';

import { ItemPrice, WeightBasedItemAvailableQuantity } from './ItemHeader';
import ItemTags from './ItemTags';
import { MenuLocaleContext } from '../../../../MenuLocaleContext';
import { ItemQuantity } from '../../../types';
import EmptyThumbail from '../../EmptyThumbnail';
import { getPurchasabilityValidationErrors } from '../utils';

type Props = {
  category: Category | null;
  className?: string;
  item: Item;
  itemInventoryCount: ItemQuantity | undefined;
  selected?: boolean;
};

const Root = styled.div`
  min-width: 550px;
  background-color: ${cssThemedValue({
    light: solidify(colors.salt100, 'light'),
    dark: solidify('#141414', 'dark'),
  })} !important;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    min-width: 320px;
  }
`;

const Header = styled.div<{ selected?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 104px;
  transition: background-color 100ms;
  background-color: ${({ selected }) =>
    selected ? colors.wolt8 : cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  margin-inline-start: 24px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 16px;
  }
`;

const StyledCheckbox = styled(React.memo(Checkbox))`
  margin-inline-start: 8px;
  margin-inline-end: 16px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 0;
  }
`;

const ThumbnailContainer = styled.div`
  margin-inline-end: 16px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-end: 8px;
  }
`;

const NameAndDescription = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  white-space: nowrap;
  padding: 16px 0;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    padding: 8px 0;
  }
`;

const NameAndTags = styled.div`
  display: flex;
  margin-block-end: 4px;
  flex-direction: row;
  align-items: center;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-block-end: 0;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
  }
`;

const Name = styled(T.Body3).attrs({ fontWeight: 'bold' })`
  align-self: center;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${cbSemanticCssVars.textPrimary};
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    align-self: normal;
  }
`;

const StyledTags = styled(ItemTags)`
  margin-inline-start: 12px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 0;
  }
`;

const Quantity = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
  margin-inline-start: 12px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 0;
  }
`;

const Description = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DragOverlayItemHeader = ({ className, category, item, itemInventoryCount, selected }: Props) => {
  const permissions = usePermissions();
  const isWoltEmployee = hasPermissions([{ resource: 'debug', operations: ['c', 'r', 'u', 'd'] }], permissions);
  const rootRef = useRef<HTMLDivElement>(null);
  const { primaryLanguage } = useContext(MenuLocaleContext);

  const name = useMemo(
    () => item.name.find(t => t.lang === primaryLanguage) || item.name[0],
    [item.name, primaryLanguage],
  );
  const description = useMemo(
    () => item.description.find(t => t.lang === primaryLanguage) || item.description[0],
    [item.description, primaryLanguage],
  );

  const { options } = useMenuData();

  const purchasabilityErrors = useMemo(() => getPurchasabilityValidationErrors(options, item), [options, item]);

  const { width } = useResizeObserver(rootRef);

  /* 24px account for the left navigation when collapsed */
  const isNarrowView = useMemo(() => width !== undefined && width <= MENU_MOBILE_BREAKPOINT - 24, [width]);

  const isBinaryInventory = useMemo(
    () => !item.inventory_mode || item.inventory_mode === 'forced_out_of_stock',
    [item.inventory_mode],
  );

  return (
    <Root className={className} ref={rootRef}>
      <Header selected={selected}>
        <Left>
          <StyledCheckbox checked={!!selected} readOnly />
          <ThumbnailContainer>
            {item.image ? (
              <LazyLoadedThumbnail alt={name.value} blurhash={item.image_blurhash} src={item.image} />
            ) : (
              <EmptyThumbail />
            )}
          </ThumbnailContainer>
          <NameAndDescription>
            <NameAndTags>
              <Name dir="auto" lang={name.lang}>
                {name.value}
              </Name>
              <StyledTags
                availabilityPeriod={item.availability_period}
                disabled={!item.enabled}
                soldOut={item.inventory_mode === 'forced_out_of_stock'}
                primaryLanguage={primaryLanguage}
                purchasabilityErrors={purchasabilityErrors}
                special={category?.id === WOLT_SPECIALS_CATEGORY_ID}
                isTiny={isNarrowView}
              />
              {!isBinaryInventory && itemInventoryCount && (
                <Quantity>
                  {!item.sell_by_weight_config && (
                    <>
                      {itemInventoryCount.available}/{itemInventoryCount.balance}{' '}
                      <FormattedMessage id="menu-item.available" />
                    </>
                  )}
                  {isWoltEmployee &&
                    item.sell_by_weight_config &&
                    item.sell_by_weight_config.input_type === 'grams' && (
                      <>
                        <WeightBasedItemAvailableQuantity itemInventoryCount={itemInventoryCount} />{' '}
                        <FormattedMessage id="menu-item.available" />
                      </>
                    )}
                  {isWoltEmployee &&
                    item.sell_by_weight_config &&
                    item.sell_by_weight_config?.input_type !== 'grams' && (
                      <>
                        {itemInventoryCount.available}/{itemInventoryCount.balance}{' '}
                        <FormattedMessage id="menu-item.available" />
                      </>
                    )}
                </Quantity>
              )}
              {isNarrowView && <ItemPrice item={item} />}
            </NameAndTags>
            {!isNarrowView && description && (
              <Description dir="auto" lang={description.lang}>
                {description.value}
              </Description>
            )}
          </NameAndDescription>
        </Left>
      </Header>
    </Root>
  );
};

export default React.memo(DragOverlayItemHeader);
